/* PERSONALISATION */

/* 
** See apps.js last lines for splider initialisation
** (needed as app.js loaded as module so cant access Splide class from here)
** Other changes:
** - st-main-product.liquid --> Add button to open personalisation modal + render personalisation snippet
** - st-table-product-cart.liquid + st-mini-cart-content.liquid --> Change bugged text in cart item to correct item properties
*/

if (!customElements.get('custom-personalisation')) {
  class CustomPersonalisation extends HTMLElement {
    constructor() {
      super();

      // Define elements
      this.bodyElem = document.querySelector('body');
      this.openButtons = document.querySelectorAll('.product-main .open_personalisation');
      this.closeIconElem = this.querySelector('.close_icon');
      this.radioFontElems = this.querySelectorAll('input[type=radio][name="font"]');
      this.textInput = this.querySelector('#InitialsInput');
      this.textInFullImageElem = this.querySelector('#initialsOnFullImage');
      this.textInZoomImageElem = this.querySelector('#initialsOnZoomImage');
      this.imageFullElem = this.querySelector('#imageFull');
      this.imagesPanelElem = this.querySelector('.images_panel');
      this.formElem = this.querySelector('#personalisationForm');
      this.engravingErrorElem = this.querySelector('#personalisationForm .personalisation_block_error');
      this.fontClassNames = Array.from(this.radioFontElems).map((radio) => radio.dataset.fontClass || `font_${radio.value}`);

      // Define product data
      this.productType = this.dataset.productType;
      this.variantId = this.dataset.variantId;
      this.customProductId = this.dataset.customProductId;
      this.collection = this.dataset.collection;
      this.allowJapaneseCharacters = this.dataset.allowJapaneseCharacters === 'true';
      this.minSigns = parseInt(this.dataset.minSigns, 10) || 2;
      this.maxSigns = parseInt(this.dataset.maxSigns, 10) || 3;
      this.lastTriggerElem = null;
      this.previousBodyOverflow = '';
      this.boundHandleDocumentKeydown = this.handleDocumentKeydown.bind(this);

      this.bindEvents();
      this.updateDialogState(false);
      this.computeTextSize();
    }

    connectedCallback() {
      document.addEventListener('keydown', this.boundHandleDocumentKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.boundHandleDocumentKeydown);
    }

    // Bind events
    bindEvents() {
      // Open personalisation button in product page
      this.openButtons.forEach((buttonElem) => {
        buttonElem.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(buttonElem);
        });
      });
      
      // Close icon in personalisation modal
      this.closeIconElem.addEventListener('click', () => this.close());

      // Handle text input change
      this.textInput.addEventListener('input', this.handleTextInput.bind(this));

      // Handle window resize
      window.addEventListener('resize', this.computeTextSize.bind(this));

      // Handle radio change
      this.radioFontElems.forEach(radio => radio.addEventListener('change', () =>  {
        this.imagesPanelElem.classList.remove(...this.fontClassNames);
        this.imagesPanelElem.classList.add(radio.dataset.fontClass || `font_${radio.value}`);
      }));

      // Handle form submit
      this.formElem.addEventListener('submit', (e) => {
        e.preventDefault();

        const formData = new FormData(e.target);
        const initials = this.sanitizeInput(formData.get('initials') || '');
        const signCount = this.getTextSigns(initials).length;
        this.textInput.value = initials;
        this.engravingErrorElem.style.display = "none";
        if (signCount < this.minSigns || signCount > this.maxSigns) {
          this.engravingErrorElem.style.display = "block";
          return;
        }

        if (window.dupontPersonalization?.enabled) {
          window.DupontPersonalizationCart.add(this, {
            letters: initials, font: formData.get('font'), collection: this.collection
          });
          return;
        }

        const drawer = document.querySelector('cart-drawer')
        
        const items = {
          "items": [
            {
              quantity: 1,
              id: this.customProductId,
              properties: {
                "Product": this.variantId,
                "Letters": initials,
                "Font": formData.get('font'),
                "Collection": this.collection
              }
            },
            {
              quantity: 1,
              id: this.variantId,
              properties: {
                "Letters": initials,
                "Font": formData.get('font')
              }
            }
          ]
        }
        const headers = new Headers({ 'Content-Type': 'application/json' });
        const request = {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(items)
        };
        fetch('/cart/add.js', request)
        .then(async (response) => {
          if (!response.ok) throw new Error(`Cart API: ${response.status}`);
          await window.DupontPersonalizationCart.notifyAdded(this.variantId);
          window.ShpCustom.Cart.getCart();
          // location.href = '/cart';
        })
        .catch((error) => console.error('Personalisation: ajout au panier impossible.', error));

        this.close();
      });
    }

    updateDialogState(isOpen) {
      this.classList.toggle('open', isOpen);
      this.toggleAttribute('inert', !isOpen);
      this.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      this.openButtons.forEach((buttonElem) => {
        buttonElem.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }

    handleDocumentKeydown(event) {
      const isEscape = event.key === 'Escape' || event.key === 'Esc' || event.code === 'Escape';
      if (!isEscape || !this.classList.contains('open')) {
        return;
      }

      event.preventDefault();
      this.close();
    }

    // Compute text size
    computeTextSize() {
      const imageWidth = this.imageFullElem.getBoundingClientRect().width;
      const imageHeight = this.imageFullElem.getBoundingClientRect().height;

      // Set box and font size
      let boxWidth;
      let boxHeight
      switch (this.productType) {
        case 'Pen_cap':
          boxWidth = imageWidth * 0.0991;
          boxHeight = imageHeight * 0.0826;
          this.textInFullImageElem.style.width = `${boxWidth}px`;
          this.textInFullImageElem.style.height = `${boxHeight}px`;
          this.textInFullImageElem.style.setProperty('--fontSize', `${boxWidth / 3}px`);

          boxWidth = imageWidth * 0.2325;
          boxHeight = imageHeight * 0.1865;
          this.textInZoomImageElem.style.width = `${boxWidth}px`;
          this.textInZoomImageElem.style.height = `${boxHeight}px`;
          this.textInZoomImageElem.style.setProperty('--fontSize', `${boxWidth / 3}px`);
          break;

        case 'Pen_ring':
          boxWidth = imageWidth * 0.1346;
          boxHeight = imageHeight * 0.1001;
          this.textInFullImageElem.style.width = `${boxWidth}px`;
          this.textInFullImageElem.style.height = `${boxHeight}px`;
          this.textInFullImageElem.style.setProperty('--fontSize', `${boxWidth / 3}px`);

          boxWidth = imageWidth * 0.5683;
          boxHeight = imageHeight * 0.4253;
          this.textInZoomImageElem.style.width = `${boxWidth}px`;
          this.textInZoomImageElem.style.height = `${boxHeight}px`;
          this.textInZoomImageElem.style.setProperty('--fontSize', `${boxWidth / 3}px`);
          break;

        case 'Leather':
          boxWidth = imageWidth * 0.0926;
          boxHeight = imageHeight * 0.1335;
          this.textInFullImageElem.style.width = `${boxWidth}px`;
          this.textInFullImageElem.style.height = `${boxHeight}px`;
          this.textInFullImageElem.style.setProperty('--fontSize', `${boxWidth / 2.5}px`);

          boxWidth = imageWidth * 0.2404;
          boxHeight = imageHeight * 0.3141;
          this.textInZoomImageElem.style.width = `${boxWidth}px`;
          this.textInZoomImageElem.style.height = `${boxHeight}px`;
          this.textInZoomImageElem.style.setProperty('--fontSize', `${boxWidth / 2.5}px`);
          break;
        case 'Wallet_card':
          boxWidth = imageWidth * 0.0926;
          boxHeight = imageHeight * 0.1335;
          this.textInFullImageElem.style.width = `${boxWidth}px`;
          this.textInFullImageElem.style.height = `${boxHeight}px`;
          this.textInFullImageElem.style.setProperty('--fontSize', `${boxWidth / 2.5}px`);

          boxWidth = imageWidth * 0.2404;
          boxHeight = imageHeight * 0.3141;
          this.textInZoomImageElem.style.width = `${boxWidth}px`;
          this.textInZoomImageElem.style.height = `${boxHeight}px`;
          this.textInZoomImageElem.style.setProperty('--fontSize', `${boxWidth / 2.5}px`);
          break;

        case 'Lighters':
        case 'ligne2':
        case 'legrand':
        case 'biggy':
        case 'slimmy':
        case 'twiggy':
        case 'initial':
          boxWidth = imageWidth * 0.0926;
          boxHeight = imageHeight * 0.1335;
          this.textInFullImageElem.style.width = `${boxWidth}px`;
          this.textInFullImageElem.style.height = `${boxHeight}px`;
          this.textInFullImageElem.style.setProperty('--fontSize', `${boxWidth / 2.5}px`);

          boxWidth = imageWidth * 0.2404;
          boxHeight = imageHeight * 0.3141;
          this.textInZoomImageElem.style.width = `${boxWidth}px`;
          this.textInZoomImageElem.style.height = `${boxHeight}px`;
          this.textInZoomImageElem.style.setProperty('--fontSize', `${boxWidth / 2.5}px`);
          break;
        case 'cigarcase':
        default:
          boxWidth = imageWidth * 0.0926;
          boxHeight = imageHeight * 0.1335;
          this.textInFullImageElem.style.width = `${boxWidth}px`;
          this.textInFullImageElem.style.height = `${boxHeight}px`;
          this.textInFullImageElem.style.setProperty('--fontSize', `${boxWidth / 2.5}px`);

          boxWidth = imageWidth * 0.2404;
          boxHeight = imageHeight * 0.3141;
          this.textInZoomImageElem.style.width = `${boxWidth}px`;
          this.textInZoomImageElem.style.height = `${boxHeight}px`;
          this.textInZoomImageElem.style.setProperty('--fontSize', `${boxWidth / 2.5}px`);
          break;
      }
    }

    getTextSigns(text) {
      if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
        return Array.from(segmenter.segment(text), ({ segment }) => segment);
      }

      return Array.from(text);
    }

    sanitizeInput(text) {
      const allowedCharsPattern = this.allowJapaneseCharacters
        ? /[^a-zA-Z\u3040-\u309F\u3099\u309A\u30A0-\u30FF\u31F0-\u31FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\uFF66-\uFF9D]/g
        : /[^a-zA-Z]/g;

      const cleanText = text.replace(allowedCharsPattern, '');
      const signs = this.getTextSigns(cleanText).slice(0, this.maxSigns);

      return signs.join('').replace(/[a-z]/g, (letter) => letter.toUpperCase());
    }

    // Handle text input change
    handleTextInput() {
      const cleanText = this.sanitizeInput(this.textInput.value);
      this.textInput.value = cleanText;

      const signs = this.getTextSigns(cleanText);


      // Display only if 2 or 3 signs

      if (signs.length < this.minSigns || signs.length > this.maxSigns) {
        this.textInFullImageElem.textContent = "";
        this.textInZoomImageElem.textContent = "";
        return;
      }

      // Update text in image
      this.textInFullImageElem.innerHTML = "";
      this.textInFullImageElem.classList.remove("nb_letters_2");
      this.textInFullImageElem.classList.remove("nb_letters_3");
      this.textInFullImageElem.classList.add(`nb_letters_${signs.length}`);
      this.textInZoomImageElem.innerHTML = "";
      this.textInZoomImageElem.classList.remove("nb_letters_2");
      this.textInZoomImageElem.classList.remove("nb_letters_3");
      this.textInZoomImageElem.classList.add(`nb_letters_${signs.length}`);
      
      for (let i = 0; i < signs.length; i++) {
        this.textInFullImageElem.innerHTML += `<span class="letter letter_${i + 1}">${signs[i]}</span>`;
        this.textInZoomImageElem.innerHTML += `<span class="letter letter_${i + 1}">${signs[i]}</span>`;
      }
    }

    // Open the personalisation modal
    open(triggerElem = null) {
      this.lastTriggerElem = triggerElem || document.activeElement;
      this.previousBodyOverflow = this.bodyElem.style.overflow;
      this.updateDialogState(true);
      this.bodyElem.style.overflow = 'hidden';
      this.computeTextSize();
      window.requestAnimationFrame(() => {
        this.textInput?.focus();
      });
    }

    // Close the personalisation modal
    close() {
      this.updateDialogState(false);
      this.bodyElem.style.overflow = this.previousBodyOverflow || '';

      if (this.lastTriggerElem && typeof this.lastTriggerElem.focus === 'function') {
        this.lastTriggerElem.focus();
      }
    }
  }

  customElements.define('custom-personalisation', CustomPersonalisation);
}
